// =============================================================================
// Pasek narzędzi edytora układów.
//
// WSTAWIANIE JEST JEDNYM KLIKNIĘCIEM, nie rozwijaną listą. Do 2026-08-26
// elementy dodawało się `<Select>`-em z pozycją „+ Dodaj element", który
// rozciągał się na całą szerokość paska i wyglądał jak pole formularza —
// dwa kliknięcia i zero informacji o tym, co w ogóle wolno wstawić. Rząd
// ikon z podpisem mówi to wprost i mieści się w jednej linii.
//
// POWIĘKSZENIE JEST LICZBĄ, nie bezimiennym suwakiem. `<input type="range">`
// od 1 do 8 nie mówił ani ile jest teraz, ani co znaczy „5" — a znaczyło
// „5 px na milimetr", czyli wielkość, której nie da się z niczym porównać.
// Dziś obok przycisków stoi procent względem wydruku 1:1 i przycisk
// „Dopasuj", bo to jest ta czynność, którą naprawdę się wykonuje.
// =============================================================================

import type { LayoutNode } from '@insolitum/print-templates';
import { Button, Switch, cn } from '@insolitum/ui';
import {
  Barcode,
  CalendarClock,
  Image as ImageIcon,
  Maximize2,
  Minus,
  Plus,
  QrCode,
  Redo2,
  Slash,
  Square,
  Table,
  Type,
  Undo2,
  type LucideIcon,
} from 'lucide-react';

import { SKALA_1_DO_1 } from './stan';

/** Co wolno wstawić. `tabela` nie jest rodzajem węzła — to gotowy zestaw. */
export type DoWstawienia = LayoutNode['kind'] | 'tabela' | 'chwila';

const WSTAWKI: Array<{ co: DoWstawienia; ikona: LucideIcon; etykieta: string; tytul: string }> = [
  { co: 'text', ikona: Type, etykieta: 'Tekst', tytul: 'Tekst — literał albo zmienna wzoru' },
  { co: 'tabela', ikona: Table, etykieta: 'Tabela', tytul: 'Tabela: nagłówek, linia i wiersze' },
  { co: 'image', ikona: ImageIcon, etykieta: 'Obraz', tytul: 'Logo organizacji albo wgrany zasób' },
  {
    co: 'chwila',
    ikona: CalendarClock,
    etykieta: 'Data',
    tytul: 'Chwila wydruku — data, godzina albo obie (kształt zmienisz we właściwościach)',
  },
  { co: 'barcode', ikona: Barcode, etykieta: 'Kod', tytul: 'Kod kreskowy (Code 128 / EAN-13)' },
  { co: 'qr', ikona: QrCode, etykieta: 'QR', tytul: 'Kod QR' },
  { co: 'line', ikona: Slash, etykieta: 'Linia', tytul: 'Linia pozioma albo pionowa' },
  { co: 'box', ikona: Square, etykieta: 'Ramka', tytul: 'Ramka (obrys prostokąta)' },
];

function Narzedzie({
  ikona: Ikona,
  tytul,
  wylaczony,
  onClick,
}: {
  ikona: LucideIcon;
  tytul: string;
  wylaczony?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={tytul}
      aria-label={tytul}
      disabled={wylaczony}
      onClick={onClick}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-ids-lg transition-colors',
        'text-aurora-muted hover:bg-aurora-bg-subtle hover:text-aurora-text',
        'disabled:pointer-events-none disabled:opacity-30'
      )}
    >
      <Ikona className="h-4 w-4" />
    </button>
  );
}

function Rozdzielacz() {
  return <span className="mx-1 h-6 w-px shrink-0 bg-aurora-border" aria-hidden />;
}

export function PasekNarzedzi({
  onWstaw,
  onCofnij,
  onPonow,
  mozeCofnac,
  mozePonowic,
  siatka,
  onSiatka,
  skala,
  onSkala,
  onDopasuj,
  wymiary,
}: {
  onWstaw: (co: DoWstawienia) => void;
  onCofnij: () => void;
  onPonow: () => void;
  mozeCofnac: boolean;
  mozePonowic: boolean;
  siatka: boolean;
  onSiatka: (wl: boolean) => void;
  skala: number;
  onSkala: (s: number) => void;
  onDopasuj: () => void;
  wymiary: { szer: number; wys: number };
}) {
  const procent = Math.round((skala / SKALA_1_DO_1) * 100);

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-aurora-border px-3 py-2">
      <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-aurora-subtle">
        Wstaw
      </span>
      {WSTAWKI.map(({ co, ikona: Ikona, etykieta, tytul }) => (
        <button
          key={co}
          type="button"
          title={tytul}
          onClick={() => onWstaw(co)}
          className={cn(
            'flex h-8 items-center gap-1.5 rounded-ids-lg px-2 text-xs transition-colors',
            'text-aurora-muted hover:bg-aurora-bg-subtle hover:text-aurora-text'
          )}
        >
          <Ikona className="h-4 w-4 shrink-0" />
          <span className="hidden lg:inline">{etykieta}</span>
        </button>
      ))}

      <Rozdzielacz />

      <Narzedzie ikona={Undo2} tytul="Cofnij" wylaczony={!mozeCofnac} onClick={onCofnij} />
      <Narzedzie ikona={Redo2} tytul="Ponów" wylaczony={!mozePonowic} onClick={onPonow} />

      <Rozdzielacz />

      <Switch label="Siatka" checked={siatka} onChange={(e) => onSiatka(e.target.checked)} />

      <div className="ml-auto flex items-center gap-1">
        <span className="mr-1 hidden text-[11px] text-aurora-subtle xl:inline">
          {wymiary.szer} × {wymiary.wys} mm
        </span>
        <Narzedzie
          ikona={Minus}
          tytul="Pomniejsz"
          onClick={() => onSkala(skala / 1.25)}
          wylaczony={procent <= 10}
        />
        {/* Procent, nie „px/mm": jedyna liczba, którą człowiek umie odnieść do
            czegokolwiek, to „ile razy większe od wydruku". */}
        <span className="w-12 text-center text-xs tabular-nums text-aurora-muted">{procent}%</span>
        <Narzedzie ikona={Plus} tytul="Powiększ" onClick={() => onSkala(skala * 1.25)} />
        <Button variant="ghost" size="sm" onClick={onDopasuj} title="Dopasuj do okna">
          <Maximize2 className="h-4 w-4" />
          <span className="ml-1.5 hidden md:inline">Dopasuj</span>
        </Button>
      </div>
    </div>
  );
}
